/**
 * pt-BR date/time formatting helpers.
 *
 * All timestamps in the app are epoch milliseconds (number). These helpers
 * render them in the app's default timezone (America/Sao_Paulo) using the
 * `pt-BR` locale, and provide small utilities for datetime-local <input> values.
 */
import { DEFAULT_TIMEZONE } from '@agente/shared'

const TZ = DEFAULT_TIMEZONE
const LOCALE = 'pt-BR'

const dateTimeFmt = new Intl.DateTimeFormat(LOCALE, {
  timeZone: TZ,
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
})

const timeFmt = new Intl.DateTimeFormat(LOCALE, {
  timeZone: TZ,
  hour: '2-digit',
  minute: '2-digit',
})

const dayFmt = new Intl.DateTimeFormat(LOCALE, {
  timeZone: TZ,
  weekday: 'long',
  day: '2-digit',
  month: 'long',
})

const relativeFmt = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' })

const currencyBRLFmt = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: 'BRL',
})

/** "R$ 12,34" — valor monetário em reais no padrão pt-BR. */
export function formatCurrencyBRL(value: number): string {
  return currencyBRLFmt.format(Number.isFinite(value) ? value : 0)
}

/** "24 fev., 14:30" */
export function formatDateTime(epochMillis: number): string {
  return dateTimeFmt.format(epochMillis)
}

/** "14:30" */
export function formatTime(epochMillis: number): string {
  return timeFmt.format(epochMillis)
}

/** "segunda-feira, 24 de fevereiro" */
export function formatDay(epochMillis: number): string {
  return dayFmt.format(epochMillis)
}

/**
 * Human relative time ("há 5 min", "em 2 h", "amanhã"). Falls back to an
 * absolute date/time beyond ~7 days out.
 */
export function formatRelative(epochMillis: number, now = Date.now()): string {
  // Guard against missing/non-numeric timestamps (e.g. an unresolved server
  // timestamp or a legacy doc): Intl.RelativeTimeFormat.format() THROWS on a
  // non-finite value, which would crash the whole conversation list render.
  if (!Number.isFinite(epochMillis)) return ''
  const diffMs = epochMillis - now
  const absMs = Math.abs(diffMs)
  const minute = 60_000
  const hour = 60 * minute
  const day = 24 * hour

  if (absMs < minute) return 'agora'
  if (absMs < hour) return relativeFmt.format(Math.round(diffMs / minute), 'minute')
  if (absMs < day) return relativeFmt.format(Math.round(diffMs / hour), 'hour')
  if (absMs < 7 * day) return relativeFmt.format(Math.round(diffMs / day), 'day')
  return formatDateTime(epochMillis)
}

/**
 * Convert an epoch-millis timestamp to the `YYYY-MM-DDTHH:mm` string a
 * `<input type="datetime-local">` expects, expressed in the app timezone.
 */
export function toDateTimeLocalValue(epochMillis: number): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(epochMillis)
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '00'
  // en-CA gives ISO-ish ordering; hour may come back as "24" at midnight.
  const hour = get('hour') === '24' ? '00' : get('hour')
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`
}

/**
 * Parse a `<input type="datetime-local">` value (interpreted in the app
 * timezone) into epoch milliseconds. Returns `null` for empty/invalid input.
 */
export function fromDateTimeLocalValue(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value)
  if (!match) return null
  const [, y, mo, d, h, mi] = match
  // Build the instant as if the wall-clock components were UTC, then correct by
  // the app timezone's offset at that instant so the value means "this local time
  // in America/Sao_Paulo".
  const asUtc = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi))
  const offsetMs = timezoneOffsetMs(asUtc)
  return asUtc - offsetMs
}

/** Offset (ms) of the app timezone from UTC at a given instant. */
function timezoneOffsetMs(epochMillis: number): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  const parts = dtf.formatToParts(epochMillis)
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0)
  const hour = get('hour') === 24 ? 0 : get('hour')
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    hour,
    get('minute'),
    get('second'),
  )
  return asUtc - epochMillis
}
